import Phaser from 'phaser';
import { GameObjectManager } from './GameObjectManager';
import { environment } from 'environment';

// File paths
const tiledPath: string = 'assets';
const tilesetName: string = 'AtlasTerrain';

export default class ChunkedScene extends Phaser.Scene {
  // May not need these
  player: Phaser.GameObjects.Sprite;

  // Chunking parameters
  static chunkWidth: integer;
  static chunkHeight: integer;
  static tileWidth: integer;
  static tileHeight: integer;
  static nbChunksHorizontal: integer;
  static nbChunksVertical: integer;

  // Class Instance Variables
  maps: object = {};
  displayedChunks: Array<any> = [];
  objectManager: GameObjectManager;

  constructor(config) {
    super(config);
  }

  // Determines the ID of the chunk on which the player character is based on its coordinates in the world
  static computeChunkID(x: integer, y: integer) {
    var tileX = Math.floor(x / 32);
    var tileY = Math.floor(y / 32);
    var chunkX = Math.floor(tileX / ChunkedScene.chunkWidth);
    var chunkY = Math.floor(tileY / ChunkedScene.chunkHeight);
    return chunkY * ChunkedScene.nbChunksHorizontal + chunkX;
  }

  static getNearestTileIndices(x: number, y: number){
    const globalTileX = Math.floor(x / 32);
    const globalTileY = Math.floor(y / 32);
    const chunkTileX = globalTileX % ChunkedScene.chunkWidth;
    const chunkTileY = globalTileY % ChunkedScene.chunkHeight;
    return {col: chunkTileX, row: chunkTileY};
  }

  // Returns the entries in secondArray that are not present in firstArray
  static findDiffArrayElements(firstArray: Array<any>, secondArray: Array<any>) {
    return firstArray.filter(function (i) {
      return secondArray.indexOf(i) < 0;
    });
  }

  updateEnvironment() {
    var chunkID = ChunkedScene.computeChunkID(this.player.x, this.player.y);
    var chunks = ChunkedScene.listAdjacentChunks(chunkID); // List the id's of the chunks surrounding the one we are in
    var newChunks = ChunkedScene.findDiffArrayElements(chunks, this.displayedChunks); // Lists the surrounding chunks that are not displayed yet (and have to be)
    var oldChunks = ChunkedScene.findDiffArrayElements(this.displayedChunks, chunks); // Lists the surrounding chunks that are still displayed (and shouldn't anymore)

    // NOTE: Cannot reference this within anonymous functions scope
    for (const c of newChunks) {
      this.load.tilemapTiledJSON(
        'chunk' + c,
        `${tiledPath}/chunks/chunk` + c + '.json'
      );
    }

    if (newChunks.length > 0) {
      this.load.start(); // Needed to trigger loads from outside of preload()
    }

    for (const c of oldChunks) {
      this.removeChunk(c);
      // let chunkX: integer = c % this.nbChunksHorizontal;
      // let chunkY: integer = Math.floor(c / this.nbChunksHorizontal);
      // this.objectManager.destroyOldObjectsByChunk(chunkX, chunkY);
    }
    this.objectManager.destroyOldObjects();
  }

  async displayChunk(key): Promise<number> {
    var map = this.make.tilemap({ key: key });

    // The first parameter is the name of the tileset in Tiled and the second parameter is the key
    // of the tileset image used when loading the file in preload.
    var tiles = map.addTilesetImage(tilesetName, 'tiles');

    // We need to compute the position of the chunk in the world
    var chunkID = parseInt(key.match(/\d+/)[0]); // Extracts the chunk number from file name
    var chunkX = (chunkID % ChunkedScene.nbChunksHorizontal) * ChunkedScene.chunkWidth;
    var chunkY =
      Math.floor(chunkID / ChunkedScene.nbChunksHorizontal) * ChunkedScene.chunkHeight;

    for (var i = 0; i < map.layers.length; i++) {
      // You can load a layer from the map using the layer name from Tiled, or by using the layer
      // index
      var layer = map.createLayer(
        i,
        tiles,
        chunkX * ChunkedScene.tileWidth,
        chunkY * ChunkedScene.tileHeight
      );

      // Trick to automatically give different depths to each layer while avoid having a layer at depth 1 (because depth 1 is for our player character)
      layer.setDepth(0);
    }

    this.maps[chunkID] = map;
    this.displayedChunks.push(chunkID);

    // Generate objects for this chunk
    this.objectManager.generateObjects(map, chunkX, chunkY);
    return chunkID;
  }

  deleteChunk(key) {
    if (key in this.maps) {
      delete this.maps[key];
      return true;
    }
    return false;
  }

  removeChunk(chunkID) {
    this.maps[chunkID].destroy();
    this.deleteChunk(chunkID);
    this.cache.tilemap.remove(`chunk${chunkID}`);
    var idx = this.displayedChunks.indexOf(chunkID);
    if (idx > -1) this.displayedChunks.splice(idx, 1);
  }

  // Returns the list of chunks surrounding a specific chunk, taking the world borders into
  // account. If you find a smarter way to do it, I'm interested!
  static listAdjacentChunks(chunkID) {
    var chunks = [];
    var isAtTop = chunkID < ChunkedScene.nbChunksHorizontal;
    var isAtBottom =
      chunkID > ChunkedScene.nbChunksHorizontal * (ChunkedScene.nbChunksVertical - 1);
    var isAtLeft = chunkID % ChunkedScene.nbChunksHorizontal == 0;
    var isAtRight =
      chunkID % ChunkedScene.nbChunksHorizontal == ChunkedScene.nbChunksHorizontal - 1;

    chunks.push(chunkID);
    if (!isAtTop) chunks.push(chunkID - ChunkedScene.nbChunksHorizontal);
    if (!isAtBottom) chunks.push(chunkID + ChunkedScene.nbChunksHorizontal);
    if (!isAtLeft) chunks.push(chunkID - 1);
    if (!isAtRight) chunks.push(chunkID + 1);
    if (!isAtTop && !isAtLeft)
      chunks.push(chunkID - 1 - ChunkedScene.nbChunksHorizontal);
    if (!isAtTop && !isAtRight)
      chunks.push(chunkID + 1 - ChunkedScene.nbChunksHorizontal);
    if (!isAtBottom && !isAtLeft)
      chunks.push(chunkID - 1 + ChunkedScene.nbChunksHorizontal);
    if (!isAtBottom && !isAtRight)
      chunks.push(chunkID + 1 + ChunkedScene.nbChunksHorizontal);
    return chunks;
  }
}
